import { Injectable, inject } from '@angular/core';

import { Participant, ParticipantOption, ParticipantPriceType, ParticipantType } from '@cds/betting-offer';
import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import {
    FixtureParticipantPriceMap,
    GreyhoundParticipantDictionary,
    GreyhoundParticipantInfo,
    HorseRaceFavouritePrefixes,
    HorseRaceSortingDirection,
    V3FixtureParticipantPriceMap,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { groupBy } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class HorsesFixtureSortingHelper {
    siteCore = inject(Sitecore);

    /**
     * sorting of the participants based on the prices supplied
     *
     * @param participants
     * @param prices
     * @returns
     *
     * @memberOf HorsesFixtureSortingHelper
     */
    sortParticipants<TParticipant extends WinGameParticipant>(participants: TParticipant[], prices: FixtureParticipantPriceMap): TParticipant[] {
        // TODO: Get rid of casting once V2 and Horses fixture are uniform
        const horsesRunners = this.getHorsesRunners(participants, prices);

        const noPriceHorses = participants.filter((p) => !prices[p.id]);
        const favouritesOnly = this.filterAndOrderFavorites(participants);

        return horsesRunners.concat(noPriceHorses).concat(favouritesOnly);
    }

    /**
     * Sorting of the participants. Win market odds first, starting price after (sorted by raceCardNumber)
     *
     * @param participants
     * @param prices
     * @returns
     *
     * @memberOf HorsesFixtureSortingHelper
     */
    sortParticipantsByWinMarket(
        participants: WinGameParticipant[],
        options: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
        isSortByParticipantId = false,
    ): WinGameParticipant[] {
        const raceRunners = this.getSortByOddsAndType(participants, options, isSortByParticipantId);
        const noPriceHorses = participants.filter((p) => !options[p.id]);
        const favouritesOnly = this.filterAndOrderFavorites(participants);

        return raceRunners.concat(noPriceHorses).concat(favouritesOnly);
    }

    private getHorsesRunners<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        options: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
    ): TParticipant[] {
        const optionV3 = options as V3FixtureParticipantPriceMap;
        const optionV1 = options as FixtureParticipantPriceMap;

        return participants
            .filter(this.validParticipant)
            .filter((p) => options[p.id])
            .map((p) => ({
                ...p,
                nativePrice: optionV3[p.id]!.nativePrice || NativePrice.fromNativePrice(optionV1[p.id]!.price),
            }))
            .sort((a, b) => this.comparePrice(a.nativePrice, b.nativePrice));
    }

    sortV3Participants<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        options: V3FixtureParticipantPriceMap,
        includeNoPriceFavHorses: boolean = true,
    ): TParticipant[] {
        const raceRunners = this.getHorsesRunners(participants, options);

        return this.getHorsesParticipants(participants, options, includeNoPriceFavHorses, raceRunners);
    }

    getFavouriteParticipantWithNames(participants: WinGameParticipant[], favoriteCount: number): WinGameParticipant[] {
        const favouriteParticipants: WinGameParticipant[] = [];
        const groupByPrice = groupBy(participants, (p) => p.nativePrice);
        let currentParticipantIndex = 0;

        Object.keys(groupByPrice).forEach((price, groupIndex) => {
            if (currentParticipantIndex < favoriteCount) {
                const participantsWithSamePrice = groupByPrice[price];
                const favouritePrefix = this.getFavouritePrefix(participantsWithSamePrice.length);
                const { updatedParticipants, updatedIndex } = this.getUpdatedParticipantsWithName(
                    participantsWithSamePrice,
                    currentParticipantIndex,
                    groupIndex,
                    favouritePrefix,
                );
                favouriteParticipants.push(...updatedParticipants);
                currentParticipantIndex = updatedIndex;
            }
        });

        return favouriteParticipants;
    }

    private getUpdatedParticipantsWithName(
        participantsWithSamePrice: WinGameParticipant[],
        currentParticipantIndex: number,
        groupIndex: number,
        favouritePrefix: string,
    ): { updatedParticipants: WinGameParticipant[]; updatedIndex: number } {
        participantsWithSamePrice.forEach((participant) => {
            participant.favouriteRank =
                this.siteCore.horseRacing.raceResulted[(favouritePrefix + (groupIndex + 1)) as keyof typeof this.siteCore.horseRacing.raceResulted];
            currentParticipantIndex++;
        });

        return { updatedParticipants: participantsWithSamePrice, updatedIndex: currentParticipantIndex };
    }

    private getFavouritePrefix(length: number): string {
        switch (length) {
            case 1:
                return HorseRaceFavouritePrefixes.Favourite;
            case 2:
                return HorseRaceFavouritePrefixes.FavouriteJoint;
            default:
                return HorseRaceFavouritePrefixes.FavouriteCo;
        }
    }

    private getHorsesParticipants<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        options: V3FixtureParticipantPriceMap,
        includeNoPriceFavHorses: boolean,
        horsesRunners: TParticipant[],
    ): TParticipant[] {
        if (includeNoPriceFavHorses) {
            const noPriceHorses = participants.filter((p) => !options[p.id] && p.properties!.type !== ParticipantType.Favourite);
            const favouritesOnly = this.filterAndOrderFavorites(participants);

            horsesRunners = horsesRunners.concat(noPriceHorses).concat(favouritesOnly);
        }

        return horsesRunners;
    }

    sortOptionParticipantsByWinMarket<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        options: V3FixtureParticipantPriceMap,
        includeNoPriceFavHorses: boolean = true,
    ): TParticipant[] {
        const raceRunners = this.getSortByOddsAndType(participants, options);

        return this.getHorsesParticipants(participants, options, includeNoPriceFavHorses, raceRunners);
    }

    private getSortByOddsAndType<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        options: V3FixtureParticipantPriceMap | FixtureParticipantPriceMap,
        isSortByParticipantId = false,
    ): TParticipant[] {
        const optionV1 = options as FixtureParticipantPriceMap;
        const optionV3 = options as V3FixtureParticipantPriceMap;
        return participants
            .filter(this.validParticipant)
            .filter((p) => options[p.id] || (p.properties?.associatedFixtureParticipantId && options[p.properties.associatedFixtureParticipantId]))
            .sort((a, b) => {
                const isV3Fixture = this.getOption(optionV3, a)?.nativePrice;
                const sortByOddsAndType = isV3Fixture
                    ? this.sortV3ByOddsAndType(this.getOption(optionV3, a)!, this.getOption(optionV3, b)!)
                    : this.sortByOddsAndType(optionV1[a.id]!, optionV1[b.id]!);
                if (sortByOddsAndType === 0) {
                    if (isSortByParticipantId) {
                        return a.participantId - b.participantId;
                    }

                    return a.properties!.raceCardNumber! - b.properties!.raceCardNumber!;
                }

                return sortByOddsAndType;
            });
    }

    private getOption(options: V3FixtureParticipantPriceMap, participant: WinGameParticipant): EventOption | undefined {
        if (options[participant.id]) {
            return options[participant.id];
        }

        if (participant.properties?.associatedFixtureParticipantId) {
            return options[participant.properties.associatedFixtureParticipantId];
        }

        return undefined;
    }

    /**
     * Sorting of the participants by odds or raceCardNumber
     *
     * @param participants
     * @param prices
     * @returns
     *
     * @memberOf HorsesFixtureSortingHelper
     */
    sortParticipantsByRaceCardNumber<TParticipant extends WinGameParticipant>(participants: TParticipant[]): TParticipant[] {
        const raceRunners = participants.filter(this.validParticipant).sort((a, b) => a.properties!.raceCardNumber! - b.properties!.raceCardNumber!);

        const favouritesOnly = this.filterAndOrderFavorites(participants);

        return raceRunners.concat(favouritesOnly);
    }

    sortParticipantsBy<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        prices: FixtureParticipantPriceMap,
        sortBy: HorseRaceSortingDirection,
    ): WinGameParticipant[] {
        if (sortBy === HorseRaceSortingDirection.Odds) {
            return this.sortParticipantsByWinMarket(participants, prices);
        }

        return this.sortParticipantsByRaceCardNumber(participants);
    }

    sortV3ParticipantsBy<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        prices: V3FixtureParticipantPriceMap,
        sortBy: HorseRaceSortingDirection,
    ): TParticipant[] {
        if (sortBy === HorseRaceSortingDirection.Odds) {
            return this.sortOptionParticipantsByWinMarket(
                participants.filter((p) => prices[p.id]),
                prices,
            );
        }

        return this.sortParticipantsByRaceCardNumber(participants.filter((p) => prices[p.id]));
    }

    sortParticipantsByTrap<TParticipant extends WinGameParticipant>(
        participants: TParticipant[],
        prices: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
        sortBy: HorseRaceSortingDirection,
    ): WinGameParticipant[] {
        if (sortBy === HorseRaceSortingDirection.Odds) {
            return this.sortParticipantsByWinMarket(participants, prices);
        }

        return this.sortParticipantsByTrapNumber(participants);
    }

    private sortByOddsAndType(a: ParticipantOption, b: ParticipantOption): number {
        if (a.priceType === ParticipantPriceType.StartingPrice && b.priceType === ParticipantPriceType.StartingPrice) {
            return 0;
        } else if (a.priceType === ParticipantPriceType.StartingPrice) {
            return 1;
        } else if (b.priceType === ParticipantPriceType.StartingPrice) {
            return -1;
        }

        return this.comparePrice(NativePrice.fromNativePrice(a.price), NativePrice.fromNativePrice(b.price));
    }

    private sortV3ByOddsAndType(a: EventOption, b: EventOption): number {
        if (a.participantPriceType === ParticipantPriceType.StartingPrice && b.participantPriceType === ParticipantPriceType.StartingPrice) {
            return 0;
        } else if (a.participantPriceType === ParticipantPriceType.StartingPrice) {
            return 1;
        } else if (b.participantPriceType === ParticipantPriceType.StartingPrice) {
            return -1;
        }

        return this.comparePrice(a.nativePrice, b.nativePrice);
    }

    private validParticipant(participant: Participant): boolean {
        return (
            participant.properties!.type === ParticipantType.Horse ||
            participant.properties!.type === ParticipantType.Golfer ||
            participant.properties!.type === ParticipantType.Trap ||
            participant.properties!.type === ParticipantType.Dog
        );
    }

    private filterAndOrderFavorites<TParticipant extends WinGameParticipant>(participants: TParticipant[]): TParticipant[] {
        return participants.filter((p) => p.properties!.type === ParticipantType.Favourite).sort((a, b) => a.participantId - b.participantId);
    }

    private comparePrice(first: NativePrice, second: NativePrice): number {
        return (first.isEmpty() && second.isEmpty()) || first.isEmpty() || first.greaterThan(second)
            ? 1
            : second.isEmpty()
              ? -1
              : first.lessThan(second)
                ? -1
                : 0;
    }

    sortParticipantsByTrapNumber(participants: WinGameParticipant[]): WinGameParticipant[] {
        const raceRunners = participants.filter(this.validParticipant).sort((a, b) => a.participantId - b.participantId);

        const favouritesOnly = this.filterAndOrderFavorites(participants);

        return raceRunners.concat(favouritesOnly);
    }

    sortParticipantsByTrapNumberOfParticipant(
        participants: WinGameParticipant[],
        participantsMapping: { [id: number]: GreyhoundParticipantInfo },
    ): WinGameParticipant[] {
        const raceRunners = participants
            .filter(this.validParticipant)
            .sort(
                (a, b) =>
                    (a.properties?.type === ParticipantType.Trap ? a.participantId : participantsMapping[a.participantId].trapNumber!) -
                    (b.properties?.type === ParticipantType.Trap ? b.participantId : participantsMapping[b.participantId].trapNumber!),
            );

        const favouritesOnly = this.filterAndOrderFavorites(participants);
        return raceRunners.concat(favouritesOnly);
    }

    sortParticipantsAlphabetically(
        participants: WinGameParticipant[],
        greyhoundTrapDogMapping: GreyhoundParticipantDictionary,
    ): WinGameParticipant[] {
        const raceRunners = participants
            .filter(this.validParticipant)
            .sort((a, b) =>
                greyhoundTrapDogMapping[a.participantId].greyhoundName.localeCompare(greyhoundTrapDogMapping[b.participantId].greyhoundName),
            );

        const favouritesOnly = this.filterAndOrderFavorites(participants);
        return raceRunners.concat(favouritesOnly);
    }
}
